import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  GlobalOutlined,
  MoreOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { Button, Status, Tooltip } from '@mm/components';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import classNames from 'classnames';
import { Field, Form, Formik, FormikConfig } from 'formik';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { HelpCenter } from '../../../../../../../../common/components/HelpCenter/HelpCenter';
import styles from './SendingDomainSetup.module.css';
import { useEspCRUDActions } from '@mm/state/actions/EspCRUDActions';
import { useSelector } from 'react-redux';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { Spin, Table } from 'antd';
import { BaseIntegration } from '../../../BaseIntegration/BaseIntegration';
import { landmineNames } from '@mm/constants/landmine-names';
import { BaseIntegrationHeader } from '../../../BaseIntegration/BaseIntegrationHeader';
import {
  Menu,
  INodeMenuItem,
} from '../../../../Automations/AutomationEdit/DialogueEditor/NodeTypes/elements/NodeMenu/NodeMenu';
import { selectAllEmailAccounts } from '@mm/state/selectors/emailAccountsSelectors';
import { useEmailAccountCRUDActions } from '@mm/state/actions/EmailAccountCRUDActions';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { EmailAccount } from '@mm/state/interfaces/emailAccount';
import { useViewActions } from '@mm/state/actions/viewActions';

const SendingDomainSetupValidationSchema = Yup.object().shape({
  domain: Yup.string().required('Required'),
});

export const SendingDomainSetup = () => {
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const emailAccounts = useSelector(selectAllEmailAccounts);
  const sendgridPlatformAccounts = emailAccounts.filter(
    acc => acc.kind === 'sendgrid_platform',
  );
  const connectedDomain = useSelector(
    (state: StoreState) => state.esp.entities,
  )?.[0];

  const { openValidateDNSModal, openGeneralConfirmModal } = useModalActions();
  const { getDomains, createDomain, destroyDomain } = useEspCRUDActions();
  const { disconnect, getAccounts } = useEmailAccountCRUDActions();
  const { retrievePage: retrieveFacebookPage } = useConnectedPageCRUDActions();
  const viewActions = useViewActions();

  const [busy, setBusy] = useState(false);

  const initialValues = {
    domain: '',
  };

  const isSubmitting = false;

  const sectionProps = {
    landmineName: landmineNames.SENDING_DOMAIN_UPGRADE,
    featureFlag: 'nativeIntegrations',
  };

  const domainStatus = {
    pending: {
      name: 'Verification in process',
      color: '#1890FF',
    },
    validated: {
      name: 'Verified',
      color: '#52C41A',
    },
    failed: {
      name: 'Not verified',
      color: '#FA541C',
    },
  };

  const onDisconnectEmailAccount = (account: EmailAccount) => {
    openGeneralConfirmModal({
      headerText: 'Confirm Account Disconnection',
      messageText: (
        <>
          <div>
            Once disconnected, all existing conversations via this email account
            will be terminated.
          </div>

          <div>
            <strong>Disconnected accounts cannot be restored.</strong>
          </div>
        </>
      ),
      onCloseHandler: async (ok: boolean) => {
        if (ok) {
          await disconnect(account);
          retrieveFacebookPage(facebookPageId);
        }
      },
      okButtonText: 'Disconnect',
      cancelButtonFirst: true,
      okButtonStyling: {
        red: true,
      },
      css: {
        primaryButton: 'integrations__diconnect-modal-primary-button',
        secondaryButton: 'integrations__confirm-modal-secondary-button',
      },
    });
  };

  const onAddOrEditSendingName = (emailAccount?: EmailAccount) => {
    viewActions.showSlideoutMenu('addSendingDomainFromName', {
      facebookPageId,
      emailAccount,
      domain: connectedDomain.domain,
    });
  };

  const onDeleteDomain = async () => {
    await destroyDomain(connectedDomain.id);
    await retrieveDomains();
    await getAccounts(facebookPageId);
  };

  const menu = [
    {
      disabled: false,
      children: (
        <span className={styles.menuItem}>
          <SyncOutlined /> Restart verification
        </span>
      ),
      onClick: () => openValidateDNSModal({ domain: connectedDomain }),
    },
    {
      disabled: false,
      children: (
        <span className={styles.menuItem}>
          <DeleteOutlined /> Delete
        </span>
      ),
      onClick: () => {
        try {
          onDeleteDomain();
        } catch (error) {}
      },
    },
  ];

  const getEmailAccountMenu = (emailAccount: EmailAccount) =>
    [
      {
        disabled: false,
        children: <span className={styles.menuItem}>Disconnect</span>,
        onClick: () => onDisconnectEmailAccount(emailAccount),
      },
    ] as INodeMenuItem[];

  const tableColumns = [
    {
      title: 'Sending Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Preview',
      dataIndex: 'emailAddress',
      key: 'emailAddress',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: 'verified' | 'pending') => (
        <Status status={status === 'verified' ? 'active' : 'inactive '} />
      ),
    },
    {
      title: 'Action',
      dataIndex: '',
      key: 'x',
      render: (item: EmailAccount) => (
        <div className={styles.actionMenu}>
          <EditOutlined
            className={styles.editIcon}
            onClick={() => onAddOrEditSendingName(item)}
          />
          <Menu
            className={classNames(styles.menu)}
            menu={getEmailAccountMenu(item)}
            direction="left"
            menuButton={
              <Button
                css={{
                  container: styles.menuButton,
                }}
                color="secondary"
              >
                <MoreOutlined />
              </Button>
            }
          />
        </div>
      ),
    },
  ];

  const statusTooltip = (
    <div>
      {Object.entries(connectedDomain?.validationResult || {})
        .filter(result => !result[1].valid)
        .map(result => {
          const resultKey = result[0];
          return (
            <div key={resultKey}>
              <span style={{ fontWeight: 500 }}>{resultKey.toUpperCase()}</span>
              :<p>{result[1].reason}</p>
            </div>
          );
        })}
    </div>
  );

  const retrieveDomains = async () => {
    setBusy(true);
    await getDomains(facebookPageId);
    setBusy(false);
  };

  const onSubmit: FormikConfig<
    typeof initialValues
  >['onSubmit'] = async values => {
    if (!connectedDomain && values.domain) {
      const res = await createDomain({ facebookPageId, domain: values.domain });
      // retrieveDomains();
      if (res) {
        openValidateDNSModal({ domain: res.domain });
      }
    }
    if (connectedDomain) {
      openValidateDNSModal({ domain: connectedDomain });
    }
  };

  useEffect(() => {
    retrieveDomains();
  }, []);

  return !connectedDomain ? (
    <div className={classNames(styles.container, { [styles.filled]: false })}>
      <div className={classNames(styles.heading, styles.mainHeading)}>
        <h3 className={styles.title}>Sending Domain</h3>
        {/* <HelpCenter helpLink={''} /> */}
      </div>

      <div className={styles.content}>
        <h4 className={styles.title}>
          Your domain
          <div className={styles.subtitle}>
            Prevent email spoofing, improve email deliverability, and protect
            your brand's reputation
          </div>
        </h4>
        <Spin spinning={busy} />
        {!busy && (
          <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            validationSchema={SendingDomainSetupValidationSchema}
          >
            {({ errors, values }) => (
              <Form className={styles.form}>
                <div className={styles.formField}>
                  <Field
                    className={classNames(styles.input, {
                      [styles.inputError]: errors?.domain,
                    })}
                    disabled={isSubmitting}
                    type="text"
                    name="domain"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={!values.domain || isSubmitting}
                  css={{ container: styles.primaryButton }}
                >
                  Validate Domain
                </Button>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </div>
  ) : (
    <BaseIntegration
      titleComponent={
        <BaseIntegrationHeader
          title="Sending Domain"
          upgradeButtonName="Add a Sending Name"
          subtitle="Prevent email spoofing, improve email deliverability, and protect your brand’s reputation"
          icon={<GlobalOutlined />}
          headerRightComponent={
            <Button
              css={{ container: 'integrations__header-add-button-secondary' }}
              onClick={e => {
                e?.stopPropagation();
                onAddOrEditSendingName();
              }}
            >
              Add a Sending Name
            </Button>
          }
          css={{ icon: 'integrations__sending-domain-icon' }}
          {...sectionProps}
        />
      }
      openOnMount
    >
      <div className={styles.connectedDomainContainer}>
        <div className={styles.mainHeading}>
          Your domain:{' '}
          {/* <HelpCenter
            className={styles.help}
            icon={<CopyOutlined />}
            helpLink={''}
          /> */}
        </div>
        <div className={styles.domainStatusRow}>
          <h4 className={styles.title}>{connectedDomain.domain}</h4>

          <Status
            className={classNames(styles.subtitle, styles.pointer)}
            status={
              <Tooltip
                title={statusTooltip}
                placement="bottom"
                overlayClassName={styles.tooltip}
              >
                {domainStatus[connectedDomain.status]?.name}
              </Tooltip>
            }
            color={domainStatus[connectedDomain.status]?.color}
            noCapitalize
          />

          <Menu
            className={classNames(styles.menu)}
            menu={menu}
            direction="bottom"
            menuButton={
              <Button
                css={{
                  container: styles.menuButton,
                }}
                color="secondary"
              >
                <MoreOutlined />
              </Button>
            }
          />
        </div>
      </div>
      {sendgridPlatformAccounts.length ? (
        <Table
          rootClassName={styles.table}
          rowClassName={styles.tableRow}
          columns={tableColumns}
          dataSource={sendgridPlatformAccounts.map(acc => ({
            key: acc.id,
            ...acc,
          }))}
          pagination={{ hideOnSinglePage: true }}
        />
      ) : (
        <div className={styles.emptyContainer}>
          <Button
            css={{ container: 'integrations__header-add-button-secondary' }}
            onClick={() => onAddOrEditSendingName()}
          >
            Add a Sending Name
          </Button>
        </div>
      )}
    </BaseIntegration>
  );
};
